import { Marker, Popup } from 'react-map-gl/mapbox';
import { useMap } from 'react-map-gl/mapbox';
import detectorGreenIcon from '../../../assets/detector-green.png';
import detectorRedIcon from '../../../assets/detector-red.png';
import detectorGrayIcon from '../../../assets/detector-gray.png';
import detectorBoxIcon from '../../../assets/detector-box.png';
import clockIcon from '../../../assets/clock.png';
import flameIcon from '../../../assets/flame.png';
import React from 'react';
import { useState , useEffect } from 'react';
import { batteryPercentage } from '../../../helpers/battery';
import { rgbToHexFromStep } from '../../../helpers/simulation';
import { authFetch } from '../../../utils';

import './DetectorMarker.css';
import CoordinatesLink from '../Base/CoordinatesLink';
import { API_URL } from '../../../config';

const DetectorMarker = ({ data, onShowSimulation, isActive, onActivate, onDeactivate, onAlarmStopped, can, onMouseEnter, onMouseLeave, showBatteries }) =>{
    const map = useMap().current?.getMap();
    const isOldDate = new Date(Date.now() - 1 * 48 * 60 * 60 * 1000);
    const hasData = data.lastObservation ? true : false;
    const isOld = hasData ? new Date(data.lastObservation.timestamp) < isOldDate : true;

    const batteryIconFilter = (() => {
        if (!showBatteries || isOld || data.activeAlarm) return undefined;
        const mv = data.lastObservation?.batteryVoltage;
        if (mv == null) return undefined;
        const pct = batteryPercentage(mv);
        if (pct > 40) return undefined;
        if (pct > 20) return 'hue-rotate(-80deg) saturate(1.5)';
        return 'hue-rotate(-130deg) saturate(1.5)';
    })();

    const handleClick = (e) => {
        e.stopPropagation();

        map.easeTo({
            center: [data.location.longitude, data.location.latitude],
            offset: [0, -100],
            duration: 500,
        });

        onActivate();
        map.once('moveend', () => map?.resize());
    };

    useEffect(() => {
        if (isActive) {
            map?.resize();
            window.dispatchEvent(new Event('resize'));
        }
    }, [isActive, map]);

    const [simulationStatus, setSimulationStatus] = useState(null);
    const [isLaunching, setIsLaunching] = useState(false);
    const [completedSimulation, setCompletedSimulation] = useState(null);
    const [isTestingAlarm, setIsTestingAlarm] = useState(false);
    

    const launchSimulation = async ({ latitude, longitude, detectorId }) => {
        setIsLaunching(true);
        setSimulationStatus('running');

        try {
            const res = await authFetch(`${API_URL}simulation/simulate`, {
            method: 'POST',
            body: JSON.stringify({ latitude, longitude, detectorId })
            });

            if (!res.ok) throw new Error('Error al lanzar la simulación');

            const data = await res.json();
            const simulationId = data.simulationId;

            // ⏳ Comienza a verificar el estado cada 5 segundos
            const interval = setInterval(async () => {
            try {
                const statusRes = await authFetch(`${API_URL}simulation/simulation/${simulationId}`);
                const statusData = await statusRes.json();
                const status = statusData.status;

                if (status === 'completed' || status === 'failed') {
                    clearInterval(interval);
                    setSimulationStatus(status);

                        try {
                        const fullRes = await authFetch(`${API_URL}simulation/simulation/${simulationId}`);
                        const fullData = await fullRes.json();
                        if (fullData?.geojson) {
                            setCompletedSimulation(fullData);
                            if (typeof onShowSimulation === 'function') {
                                onShowSimulation(fullData);
                            }
                            }

                    } catch (err) {
                        console.error('Error al cargar simulación completada:', err);
                    }
                }
            } catch (err) {
                console.error('Error al verificar estado de simulación:', err);
                clearInterval(interval);
                setSimulationStatus('failed');
            }
            }, 5000);
        } catch (err) {
            console.error('Error al lanzar simulación:', err);
            setSimulationStatus('failed');
        }
    };

    useEffect(() => {
        if (!map || !completedSimulation?.geojson) return;

        const features = completedSimulation.geojson.features.reverse();

        if (!map.getSource('completed-simulation')) {
            map.addSource('completed-simulation', {
            type: 'geojson',
            data: completedSimulation.geojson
            });

            features.forEach((feature, i) => {
            const step = feature.properties.step ?? 1;
            const color = feature.properties.fillColor ?? rgbToHexFromStep(step, completedSimulation.steps);

            map.addLayer({
                id: feature.properties.id + '-completed-fill',
                type: 'fill',
                source: 'completed-simulation',
                filter: ['==', ['get', 'id'], feature.properties.id],
                paint: {
                'fill-color': color,
                'fill-opacity': 0.4
                }
            });
            });
        }
        }, [map, completedSimulation]);




    return (
        <>
            <Marker
                longitude={data.location.longitude}
                latitude={data.location.latitude}
                className={"detector-marker" + ( hasData && !isOld && data.activeAlarm ? " pulse" : "") } // Añade la clase "pulse" si hay alarma
            >  
                <img 
                    src={
                        isOld
                            ? detectorGrayIcon
                            : data.activeAlarm
                            ? detectorRedIcon
                            : detectorGreenIcon
                        }

                    alt="Smoke Detector"
                    className='detector-marker-image'
                    onClick={handleClick}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    style={batteryIconFilter ? { filter: batteryIconFilter } : undefined}
                />
            </Marker>

            {isActive &&  (
            <Popup
                longitude={data.location.longitude}
                latitude={data.location.latitude}
                onClose={onDeactivate}
                closeOnClick={true}
                anchor="top"
                >
                <div style={{ fontFamily: "'Inter', sans-serif", maxWidth: '200px', minWidth: '150px' }}>
                    <h3 style={{ marginBottom: '4px', fontSize: '18px' }}>Detector</h3>
                    <img
                    src={detectorBoxIcon}
                    alt="Detector Box"
                    style={{
                        width: '60px',
                        height: '60px',
                        marginBottom: '10px',
                        float: 'right',
                        marginRight: '10px'
                    }}
                    />
                    <span style={{ fontSize: '18px' }}>
                    {data.location.longName.split(' - ').map((segment, index) => (
                        <React.Fragment key={index}>
                        {segment}
                        <br />
                        </React.Fragment>
                    ))}
                    </span>
                    {can(['admin']) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>
                                {data.detectorId}
                            </span>
                            {(() => {
                                const mv = data.lastObservation?.batteryVoltage;
                                if (mv == null) return null;
                                const pct = batteryPercentage(mv);
                                const color = pct > 40 ? '#16a34a' : pct > 20 ? '#d97706' : '#dc2626';
                                return (
                                    <span style={{ fontSize: '12px', color, fontWeight: 600 }}>
                                        🔋 {pct}%
                                    </span>
                                );
                            })()}
                        </div>
                    )}
                    <div style={{ marginTop: '8px' }}>
                    {hasData && data.activeAlarm && (() => {
                        const h = parseInt(new Date().toLocaleString('en-US', { timeZone: 'Europe/Madrid', hour: 'numeric', hour12: false }));
                        const isNight = h >= 22 || h < 8;
                        return (
                        <p>
                            <span style={{ fontSize: '20px', fontWeight: 'bold', color: 'red' }} className="pulse">
                                ¡Humo detectado!
                            </span>
                            <br />
                            <span style={{ fontSize: '16px', fontWeight: 'bold', color: 'red' }}>
                                {isNight
                                    ? 'Los cañones se han activado automáticamente'
                                    : 'Activar los cañones en modo Preventivo 25%'}
                            </span>
                        </p>
                        );
                    })()}
                    <p title="Temperatura registrada por el sensor">
                        <span style={{ fontSize: '30px', fontWeight: 'bold' }}>
                        {hasData ? data.lastObservation.temperature : 'N/A'}
                        </span>{' '}
                        ºC
                    </p>
                    <p title="Humedad registrada por el sensor">
                        <span style={{ fontSize: '30px', fontWeight: 'bold' }}>
                        {hasData ? data.lastObservation.humidity : 'N/A'}
                        </span>{' '}
                        %
                    </p>
                    </div>
                    <CoordinatesLink latitude={data.location.latitude} longitude={data.location.longitude} />
                    <p
                    style={{
                        color: isOld ? 'red' : '#333',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px'
                    }}
                    >
                    <img src={clockIcon} alt="clock" style={{ height: '15px', width: '15px' }} />{' '}
                    {new Date(hasData ? data.lastObservation.timestamp : null).toLocaleString('es-ES')}
                    </p>
                    {can(['simulation-viewer','admin']) && hasData && data.activeAlarm && (
                    <p>
                        <img src={flameIcon} alt="flame" style={{ height: '20px', marginRight: '7px' }} />
                        <button
                            type="button"
                            className="link-button"
                            onClick={async () => {
                                try {
                                const res = await authFetch(`${API_URL}simulation/simulations`);
                                const allSimulations = await res.json();

                                const latest = allSimulations
                                    .filter(sim => sim.detectorId === data.detectorId && sim.status === 'completed')
                                    .sort((a, b) => new Date(b.time) - new Date(a.time))[0];

                                if (!latest) {
                                    alert('No hay simulaciones completadas para este detector.');
                                    return;
                                }

                                const fullRes = await authFetch(`${API_URL}simulation/simulation/${latest.simulationId}`);
                                const fullData = await fullRes.json();

                                if (fullData?.geojson && map) {
                                    setCompletedSimulation(fullData);
                                    if (typeof onShowSimulation === 'function') {
                                    onShowSimulation(fullData); // ← envía al padre
                                    }

                                }
                                } catch (err) {
                                console.error('Error al cargar la simulación más reciente:', err);
                                alert('No se pudo cargar la simulación.');
                                }
                            }}
                            >
                            Ver predicción del incendio
                        </button>


                    </p>
                    )}

                    {/* Botón para parar la alarma */}
                    {hasData && data.activeAlarm && (
                        <button
                            onClick={async () => {
                                try {
                                    const res = await authFetch(`${API_URL}smoke-detectors/${data.detectorId}/stopAlarm`, {
                                        method: 'PUT'
                                    });
                                    if (!res.ok) throw new Error('Error al desactivar la alarma');
                                    alert('Alarma desactivada correctamente.');
                                    onAlarmStopped?.();
                                    onDeactivate();
                                } catch (err) {
                                    console.error('Error al desactivar la alarma:', err);
                                    alert('No se pudo desactivar la alarma.');
                                }
                            }}
                            style={{
                                marginTop: '12px',
                                padding: '6px 12px',
                                backgroundColor: '#d32f2f',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                width: '100%'
                            }}
                        >
                            Detener Alarma
                        </button>
                    )}

                    {/* Botón para testear alarma */}
                    {can(['alarm-tester', 'admin']) && (
                        <button
                            onClick={async () => {
                                if (!window.confirm(`¿Seguro que quieres lanzar una alarma de test para el detector "${data.location?.longName || data.detectorId}"?`)) return;
                                setIsTestingAlarm(true);
                                try {
                                    const res = await authFetch(`${API_URL}smoke-detectors/${data.detectorId}/test-alarm`, {
                                        method: 'POST'
                                    });
                                    if (!res.ok) throw new Error('Error al lanzar el test de alarma');
                                } catch (err) {
                                    console.error('Error al lanzar test de alarma:', err);
                                } finally {
                                    setIsTestingAlarm(false);
                                }
                            }}
                            disabled={isTestingAlarm}
                            style={{
                                marginTop: '12px',
                                padding: '6px 12px',
                                backgroundColor: '#e65100',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isTestingAlarm ? 'not-allowed' : 'pointer',
                                width: '100%',
                                opacity: isTestingAlarm ? 0.6 : 1,
                            }}
                        >
                            {isTestingAlarm ? 'Lanzando...' : 'Testear alarma'}
                        </button>
                    )}

                    {/* Botón para lanzar simulación */}
                    {can(['simulation-viewer','admin']) && (
                        simulationStatus === 'running' ? (
                            <p className="simulation-running" style={{ marginTop: '12px' }}>
                                Simulando ⏳
                            </p>
                        ) : simulationStatus === 'completed' ? (
                            <p style={{ marginTop: '12px', fontWeight: 'bold', color: 'green' }}>
                                ✅ Simulación completada
                            </p>
                        ) : simulationStatus === 'failed' ? (
                            <p style={{ marginTop: '12px', fontWeight: 'bold', color: 'red' }}>
                                ❌ Simulación fallida
                            </p>
                        ) : (
                            <button
                                onClick={() =>
                                    launchSimulation({
                                        latitude: data.location.latitude,
                                        longitude: data.location.longitude,
                                        detectorId: data.detectorId
                                    })
                                }
                                style={{
                                    marginTop: '12px',
                                    padding: '6px 12px',
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    width: '100%'
                                }}
                                disabled={isLaunching}
                            >
                                Lanzar simulación
                            </button>
                        )
                    )}


                </div>
                </Popup>

        )}
      </>
    )
};

export default DetectorMarker;
